import _ from 'underscore';
import React, { useCallback, useContext, useMemo } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  mergeClasses,
  useRestoreFocusSource,
} from '@fluentui/react-components';

import {
  matchStepState,
  useWizardService,
  STEP_STATES,
  WIZARD_EVENTS,
} from '@bingads-webui-react/wizard-core';

interface IDialogConfig {
  disableConfirmPopup?: boolean,
  className?: string,
  title?: string,
  content?: string,
  confirmText?: string,
  abortText?: string,
  onConfirmFn?: () => void,
  onCancelFn?: () => void,
}

interface CancelWizardDialogProps {
  config?: IDialogConfig | ((stateMatch?) => any);
}

export const CancelWizardDialog: React.FC<CancelWizardDialogProps> = (props) => {
  const i18n = useContext(I18nContext) as any;
  const { state, send } = useWizardService();
  const showDialog = matchStepState(state, STEP_STATES.CANCELING);
  const useConfig = useMemo(() => (_.isFunction(props.config) ? props.config : () => props.config), [props.config]);

  const restoreFocusSourceAttribute = useRestoreFocusSource();

  const {
    className = null,
    title = null,
    content = null,
    confirmText = null,
    abortText = null,
    onConfirmFn = null,
    onCancelFn = null,
  } = useConfig(showDialog) || {};

  const onConfirm = useCallback(() => {
    if (onConfirmFn) {
      onConfirmFn();
    }
    send(WIZARD_EVENTS.CONFIRM_CANCEL);
  }, [send, onConfirmFn]);

  const onCancel = useCallback(() => {
    if (onCancelFn) {
      onCancelFn();
    }
    send(WIZARD_EVENTS.ABORT_CANCEL);
  }, [send, onCancelFn]);

  return (
    <Dialog
      open={showDialog}
      onOpenChange={onCancel}
      {...restoreFocusSourceAttribute}
    >
      <DialogSurface className={mergeClasses('cancel-wizard-dialog', className)}>
        <DialogBody>
          <DialogTitle>{title || i18n.getString(_TL_('Are you sure you want to exit?'))}</DialogTitle>
          <DialogContent>
            {content || i18n.getString(_TL_('You have unsaved changes that could be lost.'))}
          </DialogContent>
          <DialogActions>
            <Button
              appearance="primary"
              className="dialog-confirm"
              onClick={onConfirm}
            >
              {confirmText || i18n.getString(_TL_('Yes'))}
            </Button>
            <Button
              appearance="secondary"
              className="dialog-cancel"
              onClick={onCancel}
            >
              {abortText || i18n.getString(_TL_('No'))}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
